// src/hooks/useMessages.ts
import { useState, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { Message, Conversation, MessageInput, TypingIndicator } from '../types/message';
import { messageAPI } from '../services/messageService';

// Helper function to deduplicate conversations by ID
const deduplicateConversations = (conversations: Conversation[]): Conversation[] => {
  const seen = new Map<string, Conversation>();
  for (const conv of conversations) {
    if (conv.id && !seen.has(conv.id)) {
      seen.set(conv.id, conv);
    }
  }
  return Array.from(seen.values());
};

// Helper function to get current user ID from localStorage
const getCurrentUserId = (): string => {
  try {
    // Method 1: Get from userData in localStorage
    const userData = localStorage.getItem('userData');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (user.id || user.userId) {
          return (user.id || user.userId).toString();
        }
      } catch (parseError) {
        // Continue to next method
      }
    }
    
    // Method 2: Decode JWT token to get userId
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const parts = token.split('.');
        if (parts.length >= 2) {
          const payload = JSON.parse(atob(parts[1]));
          if (payload.userId || payload.id || payload._id) {
            return (payload.userId || payload.id || payload._id).toString();
          }
        }
      } catch (e) {
        // Token decode failed
      }
    }
  } catch (error) {
    console.error('[getCurrentUserId] Error:', error);
  }
  return '';
};

export const useMessages = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typingUsers, setTypingUsers] = useState<TypingIndicator[]>([]);

  // Append an incoming message to state if it belongs to the current active conversation
  const appendIncomingMessage = useCallback((incoming: Message) => {
    setMessages(prev => {
      // Avoid duplicates
      if (prev.some(m => m.id === incoming.id)) return prev;
      return [...prev, incoming];
    });
    // Update conversations list preview
    setConversations(prev => prev.map(conv => 
      conv.id === incoming.conversationId
        ? {
            ...conv,
            lastMessage: {
              content: incoming.content,
              timestamp: incoming.timestamp,
              senderId: incoming.senderId
            },
            updatedAt: incoming.timestamp
          }
        : conv
    ));
  }, []);

  // Load conversations
  const loadConversations = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await messageAPI.getConversations();
      
      if (response.success && response.data) {
        // Deduplicate conversations to prevent duplicates
        const deduplicated = deduplicateConversations(response.data);
        setConversations(deduplicated);
      } else {
        setError(response.message || 'Failed to load conversations');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load messages for a conversation
  const loadMessages = useCallback(async (conversationId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await messageAPI.getMessages(conversationId);
      
      if (response.success && response.data) {
        setMessages(response.data);
        
        // Set active conversation
        const conversation = conversations.find(c => c.id === conversationId);
        if (conversation) {
          setActiveConversation(conversation);
        }
      } else {
        setError(response.message || 'Failed to load messages');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [conversations]);

  // Send message
  const sendMessage = useCallback(async (messageInput: MessageInput, conversationId: string) => {
    // Generate temporary ID for optimistic message
    const tempId = `temp-${Date.now()}-${Math.random()}`;
    
    // Get current user ID for optimistic message
    const currentUserId = getCurrentUserId();
    
    if (!currentUserId) {
      setError('Unable to send message: User ID not found');
      return { success: false, error: 'User ID not found' };
    }
    
    // Create optimistic message
    const optimisticMessage: Message = {
      id: tempId,
      conversationId,
      senderId: currentUserId,
      content: messageInput.content,
      timestamp: new Date().toISOString(),
      status: {
        sent: false,  // Mark as "sending"
        delivered: false,
        read: false,
      },
      type: messageInput.type || 'text',
    };
    
    // Store previous conversation lastMessage for error rollback
    const conversation = conversations.find(c => c.id === conversationId);
    const previousLastMessage = conversation?.lastMessage;
    const previousUpdatedAt = conversation?.updatedAt;
    
    // Add to UI immediately (optimistic update)
    // Use flushSync to force immediate render - this makes the message appear instantly (0ms delay)
    // This is acceptable for optimistic updates where we want instant user feedback
    try {
      flushSync(() => {
        setMessages(prev => [...prev, optimisticMessage]);
      });
    } catch (error) {
      // Fallback: try without flushSync (shouldn't happen, but handle gracefully)
      setMessages(prev => [...prev, optimisticMessage]);
    }
    
    // Update conversation lastMessage optimistically
    setConversations(prev => 
      prev.map(conv => 
        conv.id === conversationId 
          ? {
              ...conv,
              lastMessage: {
                content: messageInput.content,
                timestamp: optimisticMessage.timestamp,
                senderId: currentUserId
              },
              updatedAt: optimisticMessage.timestamp
            }
          : conv
      )
    );
    
    try {
      // Make API call in background
      const response = await messageAPI.sendMessage(messageInput, conversationId);
      
      if (response.success && response.data) {
        // Replace optimistic message with real one, and prevent duplicates
        setMessages(prev => {
          const hasRealMessage = prev.some(m => m.id === response.data!.id);
          if (hasRealMessage) {
            // Real message already exists (maybe from WebSocket), just remove optimistic
            return prev.filter(msg => msg.id !== tempId);
          }
          // Replace optimistic with real message
          return prev.map(msg => 
            msg.id === tempId ? response.data! : msg
          );
        });
        
        // Update conversation with real message timestamp
        setConversations(prev => 
          prev.map(conv => 
            conv.id === conversationId 
              ? {
                  ...conv,
                  lastMessage: {
                    content: messageInput.content,
                    timestamp: response.data!.timestamp,
                    senderId: response.data!.senderId
                  },
                  updatedAt: response.data!.timestamp
                }
              : conv
          )
        );
        
        return { success: true };
      } else {
        // API returned error - remove optimistic message
        setMessages(prev => prev.filter(msg => msg.id !== tempId));
        
        // Revert conversation lastMessage
        setConversations(prev => 
          prev.map(conv => 
            conv.id === conversationId 
              ? {
                  ...conv,
                  lastMessage: previousLastMessage,
                  updatedAt: previousUpdatedAt || conv.updatedAt
                }
              : conv
          )
        );
        
        setError(response.message || 'Failed to send message');
        return { success: false, error: response.message };
      }
    } catch (err) {
      // Network error or exception - remove optimistic message
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
      
      // Revert conversation lastMessage
      setConversations(prev => 
        prev.map(conv => 
          conv.id === conversationId 
            ? {
                ...conv,
                lastMessage: previousLastMessage,
                updatedAt: previousUpdatedAt || conv.updatedAt
              }
            : conv
        )
      );
      
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [conversations]);

  // Mark messages as read
  const markAsRead = useCallback(async (conversationId: string, messageIds: string[]) => {
    try {
      const response = await messageAPI.markAsRead(conversationId, messageIds);
      
      if (response.success) {
        // Update local state
        setMessages(prev => 
          prev.map(msg => 
            messageIds.includes(msg.id) 
              ? { ...msg, status: { ...msg.status, read: true, readAt: new Date().toISOString() } }
              : msg
          )
        );
        
        // Update conversation unread count
        setConversations(prev => 
          prev.map(conv => 
            conv.id === conversationId 
              ? { ...conv, unreadCount: Math.max(0, conv.unreadCount - messageIds.length) }
              : conv
          )
        );
        
        return { success: true };
      } else {
        setError(response.message || 'Failed to mark messages as read');
        return { success: false, error: response.message };
      }
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  // Send typing indicator
  const sendTypingIndicator = useCallback(async (typingIndicator: TypingIndicator) => {
    try {
      const response = await messageAPI.sendTypingIndicator(typingIndicator);
      
      if (response.success) {
        // Update local typing state
        if (typingIndicator.isTyping) {
          setTypingUsers(prev => [...prev.filter(t => t.userId !== typingIndicator.userId), typingIndicator]);
        } else {
          setTypingUsers(prev => prev.filter(t => t.userId !== typingIndicator.userId));
        }
        
        return { success: true };
      } else {
        setError(response.message || 'Failed to send typing indicator');
        return { success: false, error: response.message };
      }
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  // Create new conversation with a participant
  const createConversation = useCallback(async (participantId: string, matchId?: string, matchUserData?: { firstName?: string; lastName?: string; profilePicture?: string }) => {
    try {
      // Normalize participantId for comparison
      const normalizedParticipantId = participantId.trim().toLowerCase();
      
      // Check if conversation already exists with this participant (by participant ID, not conversation ID)
      const existingConv = conversations.find(c => {
        const convParticipantId = c.participant.id?.toString() || c.participant.id;
        return convParticipantId?.trim().toLowerCase() === normalizedParticipantId;
      });
      
      if (existingConv) {
        // Update existing conversation with match user data if provided
        if (matchUserData && (matchUserData.firstName || matchUserData.lastName || matchUserData.profilePicture)) {
          const updatedConversation = {
            ...existingConv,
            participant: {
              ...existingConv.participant,
              firstName: matchUserData.firstName || existingConv.participant.firstName,
              lastName: matchUserData.lastName || existingConv.participant.lastName,
              profilePicture: matchUserData.profilePicture || existingConv.participant.profilePicture,
            },
          };
          setConversations(prev => prev.map(conv => 
            conv.id === existingConv.id ? updatedConversation : conv
          ));
          setActiveConversation(updatedConversation);
          // Load messages for the existing conversation instead of clearing
          loadMessages(existingConv.id);
          return { success: true, conversation: updatedConversation };
        } else {
          // Just open existing conversation and load messages
          setActiveConversation(existingConv);
          loadMessages(existingConv.id);
          return { success: true, conversation: existingConv };
        }
      }
      
      // Create new conversation
      const response = await messageAPI.createConversation(participantId, matchId, matchUserData);
      
      if (response.success && response.data) {
        // Ensure matchUserData is applied to the conversation
        let conversationToAdd = response.data;
        
        // If matchUserData was provided, ensure it's applied (double-check)
        if (matchUserData && (matchUserData.firstName || matchUserData.lastName || matchUserData.profilePicture)) {
          conversationToAdd = {
            ...conversationToAdd,
            participant: {
              ...conversationToAdd.participant,
              firstName: matchUserData.firstName || conversationToAdd.participant.firstName,
              lastName: matchUserData.lastName || conversationToAdd.participant.lastName,
              profilePicture: matchUserData.profilePicture || conversationToAdd.participant.profilePicture,
            },
          };
        }
        
        // Check if conversation already exists in list by ID (shouldn't happen, but just in case)
        const existingById = conversations.find(c => c.id === conversationToAdd.id);
        
        if (!existingById) {
          // Add conversation to local state with deduplication
          setConversations(prev => {
            const combined = [...prev, conversationToAdd];
            return deduplicateConversations(combined);
          });
        } else {
          // Update existing conversation with new data
          setConversations(prev => prev.map(conv => 
            conv.id === conversationToAdd.id ? conversationToAdd : conv
          ));
        }
        
        setActiveConversation(conversationToAdd);
        // Load messages for the newly created conversation
        loadMessages(conversationToAdd.id);
        
        return { success: true, conversation: conversationToAdd };
      } else {
        setError(response.message || 'Failed to create conversation');
        return { success: false, error: response.message };
      }
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [conversations, loadMessages, setActiveConversation, setConversations, setError]);

  // Set active conversation
  const setActiveConversationById = useCallback((conversationId: string) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation) {
      setActiveConversation(conversation);
      loadMessages(conversationId);
    } else if (conversationId === '') {
      // Clear active conversation
      setActiveConversation(null);
      setMessages([]);
    }
  }, [conversations, loadMessages]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    conversations,
    activeConversation,
    messages,
    loading,
    error,
    typingUsers,
    loadConversations,
    loadMessages,
    sendMessage,
    markAsRead,
    sendTypingIndicator,
    createConversation,
    setActiveConversationById,
    clearError,
    setError,
    setConversations,
    setActiveConversation,
    setMessages,
    appendIncomingMessage
  };
};